'use client';

import type { Yarn } from '@/lib/types';
import YarnSwatch from './YarnSwatch';
import { formatSkeinCount } from '@/lib/skeinFormat';
import { Chip } from './ui/Chip';

type YarnCardLabels = {
  available: string;
  inProgress: string;
  addColorway?: string;
};

interface YarnCardProps {
  yarns: Yarn[];
  totalAvailable: number;
  totalInUse: number;
  totalPartialGrams?: number;
  anyOver: boolean;
  labels: YarnCardLabels;
  onCardClick: () => void;
  onColorwayClick?: (yarn: Yarn) => void;
  onAddColorway?: () => void;
}

/**
 * YarnCard — reusable card for displaying one or more yarns (colorways) of the same base yarn.
 *
 * Extracted from yarn/page.tsx during the YarnBrowser refactoring.
 * Supports single and multi-colorway display with proper stats and swatches.
 */
export function YarnCard({
  yarns,
  totalAvailable,
  totalInUse,
  totalPartialGrams = 0,
  anyOver,
  labels,
  onCardClick,
  onColorwayClick,
  onAddColorway,
}: YarnCardProps) {
  const multi = yarns.length > 1;
  const head = yarns[0];
  const borderColor = anyOver ? 'var(--over)' : 'var(--ink)';
  const numberColor = anyOver ? 'var(--over-num)' : 'var(--ink)';
  const cleanedWeight =
    head.weight && head.weight.toLowerCase() !== "i don't know" ? head.weight : '';

  return (
    <div
      style={{
        width: '100%',
        background: 'var(--paper-2)',
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 88,
      }}
    >
      {/* Clickable header area */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick();
          }
        }}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'stretch',
          flex: 1,
          color: 'inherit',
        }}
      >
        {/* Left swatch plate */}
        <div
          style={{
            width: 72,
            flexShrink: 0,
            borderRight: `1px solid ${borderColor}`,
            background: 'var(--paper-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <YarnSwatch yarn={head} style={{ width: '100%', height: '100%' }} />
        </div>

        {/* Main content */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  lineHeight: 0.95,
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {head.name}
              </div>
              {head.brand && (
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    color: 'var(--ink-2)',
                    marginTop: 2,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {head.brand}
                </div>
              )}

              {/* Chips */}
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                }}
              >
                {head.fiber && <Chip size="xs">{head.fiber}</Chip>}
                {cleanedWeight && <Chip size="xs">{cleanedWeight}</Chip>}
                {!multi && head.colorName && (
                  <Chip size="xs">
                    {head.colorName}
                    {head.colorNumber ? ` #${head.colorNumber}` : ''}
                  </Chip>
                )}
              </div>
            </div>

            {/* Stats column */}
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 2,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span
                  style={{
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: '-0.02em',
                    color: numberColor,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {formatSkeinCount(totalAvailable, yarns[0]?.gramsPerSkein)}
                </span>
                {totalPartialGrams > 0 && (
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: numberColor,
                    }}
                  >
                    +{totalPartialGrams}g
                  </span>
                )}
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 6.5,
                  letterSpacing: '0.18em',
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {labels.available}
              </span>
              {totalInUse > 0 && (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 6.5,
                    letterSpacing: '0.18em',
                    color: anyOver ? 'var(--over-num)' : 'var(--in-use)',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {totalInUse} {labels.inProgress}
                </span>
              )}
            </div>
          </div>

          {/* Multi-colorway swatches */}
          {multi && (
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
              onClick={(e) => e.stopPropagation()}
            >
              {yarns.slice(0, 8).map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onColorwayClick?.(c)}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    border: '1px solid var(--ink)',
                    overflow: 'hidden',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <YarnSwatch yarn={c} style={{ width: '100%', height: '100%' }} />
                </button>
              ))}
              {onAddColorway && (
                <button
                  type="button"
                  onClick={onAddColorway}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    border: '1px dashed var(--ink)',
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  +
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default YarnCard;
